/**
 * Admin console (R11, ADR-038).
 *
 * Deliberately small. It answers four questions and refuses to answer anything
 * that would need individual-level data:
 *
 *   Does the journey hold together, or where do people stop?
 *   Does the wall convert, and does the newsletter offer convert with it?
 *   Which occupations are we resolving, and how confidently?
 *   Which destinations do people pick — and which gaps have no content?
 *
 * THE SUPPRESSION RULE IS NOT DECORATION. Any breakdown row with fewer than
 * five events is withheld. With four people in a cohort of nine, "IT project
 * manager, high exposure" is a name. The floor is applied here, at the point of
 * display, and again by the shape of the data — the collection this reads
 * carries no identity at all, so even the un-suppressed rows cannot be traced
 * to a person (ADR-026).
 */
import { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../lib/auth";
import { EVENTS } from "../lib/signals";
import { PANEL, NAVY, TEAL, CREAM, CREAM60, CREAM30, BORDER, AMBER, GREEN } from "../utils/constants";
import PulseLoader from "./PulseLoader";

const K = 5; // minimum group size — see the header note

const Section = ({ title, note, children }) => (
  <section style={{ marginBottom: 34 }}>
    <div style={{ fontSize: 11, color: TEAL, fontWeight: 600, letterSpacing: 1.4,
                  textTransform: "uppercase", fontFamily: "'JetBrains Mono', monospace",
                  paddingBottom: 8, borderBottom: `1px solid ${BORDER}`, marginBottom: 14 }}>
      {title}
    </div>
    {note && <p style={{ fontSize: 12, color: CREAM30, margin: "0 0 14px", lineHeight: 1.6,
                         maxWidth: "62ch" }}>{note}</p>}
    {children}
  </section>
);

const Stat = ({ n, label, sub, color = CREAM }) => (
  <div style={{ background: PANEL, border: `1px solid ${BORDER}`, borderRadius: 10,
                padding: "14px 16px", minWidth: 132, flex: "1 1 132px" }}>
    <div style={{ fontSize: 24, fontWeight: 700, color, fontFamily: "'JetBrains Mono', monospace",
                  fontVariantNumeric: "tabular-nums" }}>{n}</div>
    <div style={{ fontSize: 11.5, color: CREAM60, marginTop: 2 }}>{label}</div>
    {sub && <div style={{ fontSize: 10.5, color: CREAM30, marginTop: 3 }}>{sub}</div>}
  </div>
);

/** A breakdown table that withholds anything below the k floor. */
function Breakdown({ rows, labelHead, countHead = "events" }) {
  const shown = rows.filter(([, n]) => n >= K);
  const withheldGroups = rows.length - shown.length;
  const withheldEvents = rows.filter(([, n]) => n < K).reduce((s, [, n]) => s + n, 0);

  if (!shown.length) {
    return (
      <p style={{ fontSize: 12.5, color: CREAM60, lineHeight: 1.6 }}>
        Nothing to show yet — every group is below the minimum of {K}.
        {withheldGroups > 0 && <> {withheldGroups} group{withheldGroups === 1 ? "" : "s"} withheld.</>}
      </p>
    );
  }

  const max = Math.max(...shown.map(([, n]) => n), 1);
  return (
    <>
      <div style={{ display: "flex", fontSize: 10, color: CREAM30, letterSpacing: 1,
                    fontFamily: "'JetBrains Mono', monospace", marginBottom: 6 }}>
        <span style={{ flex: 1 }}>{labelHead.toUpperCase()}</span>
        <span style={{ width: 64, textAlign: "right" }}>{countHead.toUpperCase()}</span>
      </div>
      {shown.map(([label, n]) => (
        <div key={label} style={{ marginBottom: 7 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, fontSize: 13 }}>
            <span style={{ flex: 1, color: CREAM }}>{label}</span>
            <span style={{ width: 64, textAlign: "right", color: TEAL,
                           fontFamily: "'JetBrains Mono', monospace",
                           fontVariantNumeric: "tabular-nums" }}>{n}</span>
          </div>
          <div style={{ height: 3, background: BORDER, borderRadius: 2, marginTop: 4 }}>
            <div style={{ height: "100%", width: `${(n / max) * 100}%`, background: TEAL,
                          borderRadius: 2 }} />
          </div>
        </div>
      ))}
      {withheldGroups > 0 && (
        <p style={{ fontSize: 11, color: CREAM30, marginTop: 12, lineHeight: 1.55 }}>
          {withheldGroups} group{withheldGroups === 1 ? "" : "s"} withheld
          ({withheldEvents} event{withheldEvents === 1 ? "" : "s"}) — below the minimum of {K}.
          Small groups are where aggregates stop being anonymous.
        </p>
      )}
    </>
  );
}

const tally = (rows, key) => {
  const m = new Map();
  for (const r of rows) {
    const v = r[key];
    if (v == null) continue;
    m.set(v, (m.get(v) || 0) + 1);
  }
  return [...m.entries()].sort((a, b) => b[1] - a[1]);
};

export default function Admin({ onBack }) {
  const { user, ready } = useAuth();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ready || !user || !db) return;
    let dead = false;
    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, "signals"), orderBy("at", "desc"), limit(5000))
        );
        if (!dead) setRows(snap.docs.map((d) => d.data()));
      } catch (e) {
        if (!dead) setError(e?.code === "permission-denied"
          ? "This account is not on the admin allowlist."
          : "Couldn't load the signals collection.");
      }
    })();
    return () => { dead = true; };
  }, [ready, user]);

  if (!ready) return <PulseLoader text="One moment…" />;
  if (!user) {
    return <p style={{ color: CREAM60, fontSize: 14 }}>Sign in with an admin account to view this.</p>;
  }
  if (error) {
    return (
      <div style={{ background: `${AMBER}10`, border: `1px solid ${AMBER}40`, borderRadius: 10,
                    padding: 16, color: CREAM60, fontSize: 13, lineHeight: 1.6 }}>
        <strong style={{ color: AMBER }}>{error}</strong><br />
        The allowlist lives in <code>firestore.rules</code> and is enforced by Google's servers,
        not by this page.
      </div>
    );
  }
  if (!rows) return <PulseLoader text="Reading signals…" />;

  const count = (ev) => rows.filter((r) => r.event === ev).length;
  const parsed = count(EVENTS.INTAKE_PARSED);
  const scored = count(EVENTS.EXPOSURE_SCORED);
  const wallShown = count(EVENTS.WALL_SHOWN);
  const accounts = count(EVENTS.ACCOUNT_CREATED);
  const optIns = count(EVENTS.NEWSLETTER_OPT_IN);
  const pathways = count(EVENTS.PATHWAY_CHOSEN);
  const escalations = rows.filter((r) => r.escalated === true).length;
  const repeats = count(EVENTS.ASSESSMENT_REPEAT);
  const exported = count(EVENTS.ACCOUNT_EXPORTED);
  const deleted = count(EVENTS.ACCOUNT_DELETED);
  const moved = {
    down: count(EVENTS.DELTA_DOWN),
    up: count(EVENTS.DELTA_UP),
    flat: count(EVENTS.DELTA_FLAT),
    role: count(EVENTS.DELTA_ROLE_CHANGED),
    method: count(EVENTS.DELTA_METHOD_CHANGED),
  };
  const resolutions = rows.filter((r) => r.event === EVENTS.RESOLVED).length;

  const pct = (a, b) => (b ? `${Math.round((a / b) * 100)}%` : "—");
  const days = new Set(rows.map((r) => r.day)).size;

  return (
    <div style={{ animation: "fadeUp 0.4s ease" }}>
      <h2 style={{ color: CREAM, fontSize: 22, margin: "0 0 4px",
                   fontFamily: "'Space Grotesk', sans-serif" }}>How people are using it</h2>
      <p style={{ color: CREAM60, fontSize: 14, margin: "0 0 6px", maxWidth: "64ch" }}>
        {rows.length} events across {days} day{days === 1 ? "" : "s"}.
      </p>
      <p style={{ color: CREAM30, fontSize: 12, margin: "0 0 26px", lineHeight: 1.6, maxWidth: "64ch" }}>
        This reads a collection that contains no identity — no account, no name, no résumé text.
        Any breakdown below {K} events is withheld, because in a small cohort a specific occupation
        plus a specific score is a name.
      </p>

      <Section title="The journey"
        note="Where people stop is more useful than how many arrive. A step that loses most of its traffic is either broken or asking for something it has not yet earned.">
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
          <Stat n={parsed} label="résumés parsed" />
          <Stat n={scored} label="exposure scores" sub={`${pct(scored, parsed)} of uploads`} />
          <Stat n={wallShown} label="reached the wall" sub={`${pct(wallShown, scored)} of scores`} />
          <Stat n={accounts} label="accounts created" sub={`${pct(accounts, wallShown)} of walls`}
                color={GREEN} />
          <Stat n={pathways} label="pathways chosen" />
        </div>
      </Section>

      <Section title="The newsletter offer"
        note="Unticked by default and unbundled from the account, so this is a real opt-in rate rather than a measure of how many people did not notice a checkbox.">
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Stat n={optIns} label="opted in" sub={`${pct(optIns, accounts)} of accounts`} color={TEAL} />
        </div>
      </Section>

      <Section title="Resolution quality"
        note="Escalation is not failure — it is the resolver admitting it cannot tell two occupations apart, which is the behaviour we want. A rate near 100% means the deterministic tier is carrying nothing, and that IS a problem.">
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
          <Stat n={resolutions} label="resolutions" />
          <Stat n={escalations} label="escalated" sub={`${pct(escalations, resolutions)} of resolutions`}
                color={AMBER} />
        </div>
        <Breakdown rows={tally(rows, "confidenceBand")} labelHead="confidence band" />
      </Section>

      <Section title="Do they come back?"
        note="The question this product lives on. Everything above measures one visit, and a career tool nobody returns to has not helped anyone however good the first screen was. Premium collapse is continuous (ADR-022) — a single assessment is a photograph of something that only means anything as a film.">
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
          <Stat n={repeats} label="repeat assessments" sub={`${pct(repeats, accounts)} of accounts`}
                color={repeats ? GREEN : AMBER} />
        </div>
        {repeats === 0 ? (
          <p style={{ color: CREAM60, fontSize: 13, lineHeight: 1.6, margin: 0 }}>
            Nobody has come back yet — expected this early, and the number to watch. It stays at zero
            until someone runs a second assessment more than five minutes after their first.
          </p>
        ) : (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Stat n={moved.down} label="exposure fell" color={GREEN} />
            <Stat n={moved.up} label="exposure rose" color={AMBER} />
            <Stat n={moved.flat} label="unchanged" />
            <Stat n={moved.role} label="changed role" sub="not comparable" />
            <Stat n={moved.method} label="method changed" sub="ours, not theirs" />
          </div>
        )}
      </Section>

      <Section title="Data rights"
        note="Both are reachable at #account. A deletion rate that climbs is telling you something about trust that no funnel metric will.">
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Stat n={exported} label="exports" />
          <Stat n={deleted} label="deletions" sub={`${pct(deleted, accounts)} of accounts`}
                color={deleted ? AMBER : CREAM60} />
        </div>
      </Section>

      <Section title="Which roles we are seeing"
        note="The occupations people actually land on. This is what should drive which content gets written next — and which titles the market has made ambiguous.">
        <Breakdown rows={tally(rows, "occupationCode")} labelHead="occupation code" />
      </Section>

      <Section title="Where people want to go">
        <Breakdown rows={tally(rows, "targetCode")} labelHead="destination code" />
      </Section>

      <Section title="Exposure distribution">
        <Breakdown rows={tally(rows, "exposureBand")} labelHead="band" />
      </Section>

      <button onClick={onBack}
        style={{ background: "none", border: `1px solid ${CREAM30}`, color: CREAM60,
                 padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontSize: 13 }}>
        ← Back to the app
      </button>
    </div>
  );
}
